/**
 * <tws-header> — header metrics and the mobile navigation drawer.
 *
 * The nav markup is always in the DOM and styled open on desktop, so this only
 * manages the small-screen drawer: open/close state, body scroll lock, focus
 * return, and Escape/outside-click dismissal. The draw countdown inside the
 * header is its own `<tws-countdown>` element.
 */
class TwsHeader extends HTMLElement {
  connectedCallback() {
    this.headerGroup = this.closest('#header-group');
    this.publishMetrics = this.publishMetrics.bind(this);
    this.publishMetrics();

    // Fonts loading and breakpoint changes both resize the header, and the
    // sticky offset and --header-height consumers need to follow.
    if (this.headerGroup) {
      this.resizeObserver = new ResizeObserver(this.publishMetrics);
      this.resizeObserver.observe(this.headerGroup);
    }

    this.toggle = this.querySelector('[data-header-toggle]');
    this.drawer = this.querySelector('[data-header-drawer]');

    if (!this.toggle || !this.drawer) return;

    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.close = this.close.bind(this);

    this.toggle.addEventListener('click', this.onToggle);
    document.addEventListener('keydown', this.onKeydown);

    // A tap on any drawer link should dismiss the drawer before navigating.
    this.drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('tws-nav-open');
    this.resizeObserver?.disconnect();
  }

  /**
   * Publishes the header measurements the rest of the theme reads.
   *
   * base.css and the product templates expect --header-height and
   * --header-group-height on <body>; the theme's own measurement pass in
   * utilities.js only knows about the stock `header-component`, which this theme
   * no longer ships, so the tws header publishes them itself.
   */
  publishMetrics() {
    const style = document.body.style;
    style.setProperty('--header-height', `${this.offsetHeight}px`);

    if (!this.headerGroup) return;

    style.setProperty('--header-group-height', `${this.headerGroup.offsetHeight}px`);

    // Everything stacked above the header in the group — the promo bar — is what
    // the sticky rule offsets by so it can scroll out of view.
    let above = 0;
    for (const child of this.headerGroup.children) {
      if (child.contains(this)) break;
      above += child.offsetHeight;
    }
    style.setProperty('--tws-header-offset', `${above}px`);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  onToggle() {
    this.isOpen ? this.close() : this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  open() {
    // The drawer fills the viewport below the header bar. Its top has to be
    // measured, not assumed: under the sticky header the promo bar above may
    // already have scrolled out of view.
    this.style.setProperty('--tws-drawer-top', `${this.getBoundingClientRect().bottom}px`);
    this.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('tws-nav-open');
    this.drawer.querySelector('a, button')?.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('tws-nav-open');
    this.toggle.focus();
  }
}

if (!customElements.get('tws-header')) {
  customElements.define('tws-header', TwsHeader);
}

// Claim / Join CTAs point at #membership-plans. Pages without that section
// (the winners page, for example) should land on the homepage plans instead.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href="#membership-plans"]');
  if (!link || document.getElementById('membership-plans')) return;

  event.preventDefault();
  window.location.assign('/#membership-plans');
});
